/**
 * Clarity Tracker — visitor analytics + session-replay-lite.
 * Implements the "Clarity Tracker — Frontend Integration Guide".
 *
 * Public API:
 *   Clarity.track(type, data)          push a custom activity event
 *   Clarity.identify(email, name, phone, meta)
 *   Clarity.formSubmit({ name, selector, fields, was_complete })
 *   Clarity.flush()                    force-flush the event buffer
 *   Clarity.spaRouteChange()           call after client-side navigation
 *   Clarity.getSessionUuid() / getPageViewUuid() / getVisitorUuid()
 */
(function () {
    'use strict';

    var API_BASE = 'https://clarity.signmakerz.com/api/tracker';
    var SESSION_TIMEOUT_MS = 30 * 60 * 1000;
    var FLUSH_INTERVAL_MS = 5 * 1000;
    var HEARTBEAT_INTERVAL_MS = 10 * 1000;
    // Time since last user input after which the tab counts as idle — engaged
    // time stops accruing at that boundary. Mirrors GA4's 30 s definition.
    var IDLE_THRESHOLD_MS = 30 * 1000;
    var MAX_BATCH = 500;
    var GEO_TIMEOUT_MS = 2000;
    var FILE_MAX_BYTES = 20 * 1024 * 1024;
    // MIMEs allowed for uploads. `.ai` and `.eps` files are reported by various
    // browsers as `application/postscript`, `application/illustrator`, or with
    // no MIME at all — accept any of those, and also fall back to an extension
    // whitelist below so a missing/unknown MIME on a valid file isn't rejected.
    var OK_FILE_MIMES = [
        'image/png', 'image/jpeg', 'image/svg+xml', 'application/pdf',
        'application/postscript', 'application/illustrator', 'application/vnd.adobe.illustrator',
        'image/x-eps',
    ];
    var OK_FILE_EXTS = ['png', 'jpg', 'jpeg', 'svg', 'pdf', 'ai', 'eps'];
    var BLOCKED_COUNTRIES = ['PK'];
    // URL paths for which tracking is skipped entirely.
    var BLOCKED_PATH_SUBSTRINGS = ['/products/'];

    function isPathBlocked(path) {
        if (!path) return false;
        for (var i = 0; i < BLOCKED_PATH_SUBSTRINGS.length; i++) {
            if (path.indexOf(BLOCKED_PATH_SUBSTRINGS[i]) !== -1) return true;
        }
        return false;
    }

    // ---------- storage helpers ----------
    function setCookie(name, value, days) {
        try {
            var d = new Date();
            d.setTime(d.getTime() + days * 86400000);
            var attrs = ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
            // Secure is required for SameSite semantics on modern browsers and makes
            // strict-privacy modes more willing to persist the cookie.
            if (location.protocol === 'https:') attrs += ';Secure';
            document.cookie = name + '=' + encodeURIComponent(value) + attrs;
        } catch (_) { }
    }
    function getCookie(name) {
        var m = document.cookie.match('(?:^|; )' + name + '=([^;]*)');
        return m ? decodeURIComponent(m[1]) : null;
    }
    // theme.liquid defines window.safeLocalStorage / window.safeSessionStorage
    // near the top of <head>. Those wrappers never throw and fall back to an
    // in-memory store if the real Storage is blocked (Chrome 3P cookie block,
    // Edge tracking prevention, Safari ITP, Incognito, enterprise policy). Use
    // them here so the tracker degrades the same way the rest of the theme does.
    function _ls() {
        try { return window.safeLocalStorage || window.localStorage || null; } catch (_) { return null; }
    }
    function _ss() {
        try { return window.safeSessionStorage || window.sessionStorage || null; } catch (_) { return null; }
    }
    function lsGet(k) { var s = _ls(); try { return s ? s.getItem(k) : null; } catch (_) { return null; } }
    function lsSet(k, v) { var s = _ls(); try { if (s) s.setItem(k, v); } catch (_) { } }
    function lsRemove(k) { var s = _ls(); try { if (s) s.removeItem(k); } catch (_) { } }

    // Session-scoped storage with a localStorage fallback. When sessionStorage
    // is unavailable we degrade to localStorage so session state still survives
    // a reload. Both are written on set so a later read finds the value in
    // whichever store is available.
    function ssGet(k) {
        var s = _ss();
        try {
            var v = s ? s.getItem(k) : null;
            if (v !== null) return v;
        } catch (_) { }
        return lsGet(k);
    }
    function ssSet(k, v) {
        var s = _ss();
        try { if (s) s.setItem(k, v); } catch (_) { }
        lsSet(k, v);
    }
    function ssRemove(k) {
        var s = _ss();
        try { if (s) s.removeItem(k); } catch (_) { }
        lsRemove(k);
    }

    // ---------- session-id persistence (fault-tolerant fallback chain) ----------
    // The session UUID has to survive a full page reload even when cookies are
    // blocked/disabled (which storage-blocking browser modes routinely do
    // alongside 3P-cookie policies). Chain, in priority order:
    //   1. Cookie              — shared across tabs, survives reload, sent to server automatically
    //   2. sessionStorage/localStorage (via ssGet/ssSet, already degrade to each other)
    //   3. URL query param     — recovers a session id a same-origin page linked with ?csid=,
    //                            useful only when the referring page appended one; best-effort
    //   4. In-memory variable  — last resort; survives only for the life of this page (no reload)
    // Reads check the chain in order and stop at the first hit. Writes fan out
    // to every mechanism so a later read still resolves the session even if
    // whichever store served the last read becomes unavailable.
    var SESSION_ID_KEY = 'clarity_session_uuid';
    var SESSION_URL_PARAM = 'csid';
    // Keep the cookie itself alive longer than the session-idle timeout.
    // Some strict-privacy browser modes drop cookies with very short lifetimes
    // (< 1 hour) as suspected trackers. The real 30-min idle expiry is
    // enforced by the server (CloseInactiveSessions) and by the last-activity
    // timestamp check below — the cookie just needs to survive a page reload.
    var SESSION_ID_TTL_DAYS = 1;
    var memorySessionUuid = null;

    function readSessionIdFromUrl() {
        try {
            var v = new URLSearchParams(location.search).get(SESSION_URL_PARAM);
            return (v && /^[0-9a-f-]{16,64}$/i.test(v)) ? v : null;
        } catch (_) { return null; }
    }

    function readSessionId() {
        return getCookie(SESSION_ID_KEY) ||
            ssGet(SESSION_ID_KEY) ||
            readSessionIdFromUrl() ||
            memorySessionUuid ||
            null;
    }

    function writeSessionId(id) {
        memorySessionUuid = id;
        setCookie(SESSION_ID_KEY, id, SESSION_ID_TTL_DAYS);
        ssSet(SESSION_ID_KEY, id);
    }

    function clearSessionId() {
        memorySessionUuid = null;
        setCookie(SESSION_ID_KEY, '', -1);
        ssRemove(SESSION_ID_KEY);
    }

    // Staleness (and thus session recovery) is judged from this timestamp, so
    // it needs the same fallback chain as the session id itself — otherwise a
    // browser with sessionStorage/localStorage blocked would read a null
    // timestamp, be judged "stale", and discard a perfectly valid cookie session.
    var ACTIVITY_KEY = 'clarity_last_activity_ts';
    var memoryLastActivity = null;

    function readLastActivity() {
        return getCookie(ACTIVITY_KEY) || ssGet(ACTIVITY_KEY) || memoryLastActivity || null;
    }

    function writeLastActivity(v) {
        memoryLastActivity = v;
        setCookie(ACTIVITY_KEY, v, SESSION_ID_TTL_DAYS);
        ssSet(ACTIVITY_KEY, v);
    }

    function uuid() {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = Math.random() * 16 | 0;
            var v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    function nowIso() { return new Date().toISOString(); }

    // ---------- identity ----------
    // Visitor UUID must survive across tabs and reloads. Read from the first
    // available store (cookie → localStorage → sessionStorage); write to every
    // store that works so a later read still resolves it if one gets blocked.
    // Without this fallback, a browser that blocks cookies would mint a new
    // visitor UUID on every reload and the backend would open a fresh session
    // each time.
    function readVisitor() {
        return getCookie('clarity_visitor_uuid') ||
            lsGet('clarity_visitor_uuid') ||
            ssGet('clarity_visitor_uuid') ||
            null;
    }
    function writeVisitor(v) {
        setCookie('clarity_visitor_uuid', v, 730);
        lsSet('clarity_visitor_uuid', v);
        ssSet('clarity_visitor_uuid', v);
    }

    var visitorUuid = readVisitor();
    if (!visitorUuid) visitorUuid = uuid();
    writeVisitor(visitorUuid);

    var sessionUuid = null;
    var pageViewUuid = null;
    var previousPageViewUuid = ssGet('clarity_page_view_uuid') || null;
    var pageEnteredAtMs = Date.now();
    var maxScroll = 0;
    var isBlocked = false;         // hard block (geo) — disables everything permanently
    var pageBlocked = false;       // soft block (per-pageview URL) — flips on SPA nav
    var listenersAttached = false;
    var intervalsStarted = false;

    // Trust the session cookie's own presence as primary. The activity
    // timestamp is a secondary idle guard — if it's missing (async write race
    // on a fast navigation, cleared by a privacy extension, etc.) we still
    // honour a valid session cookie rather than silently minting a new session
    // on every page load.
    sessionUuid = readSessionId();
    if (sessionUuid) {
        var lastActivityRaw = readLastActivity();
        var lastActivityMs = lastActivityRaw ? Date.parse(lastActivityRaw) : 0;
        var sessionIdleTooLong =
            lastActivityMs > 0 &&
            (Date.now() - lastActivityMs) > SESSION_TIMEOUT_MS;
        if (sessionIdleTooLong) {
            clearSessionId();
            sessionUuid = null;
        } else {
            // Rewrite through the full chain so cookies resync if a previous
            // write landed only in sessionStorage / memory.
            writeSessionId(sessionUuid);
        }
    }

    function touch() {
        writeLastActivity(nowIso());
        // Refresh the session cookie's rolling expiry so it doesn't outlive (or
        // die before) the sessionStorage/localStorage copy of the same id.
        if (sessionUuid) setCookie(SESSION_ID_KEY, sessionUuid, SESSION_ID_TTL_DAYS);
    }

    // ---------- event buffer ----------
    var buffer = [];

    function push(type, extra) {
        if (isBlocked || pageBlocked) return;
        if (buffer.length >= MAX_BATCH) buffer.shift();
        var evt = { type: type, occurred_at: nowIso() };
        if (extra) for (var k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) evt[k] = extra[k];
        buffer.push(evt);
        touch();
    }

    // ---------- HTTP ----------
    function post(path, body, useBeacon) {
        var url = API_BASE + path;
        var payload = JSON.stringify(body);

        if (useBeacon && navigator.sendBeacon) {
            try {
                var blob = new Blob([payload], { type: 'application/json' });
                var ok = navigator.sendBeacon(url, blob);
                if (ok) return Promise.resolve(null);
            } catch (_) { }
        }

        return fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: payload,
            keepalive: true,
        }).then(function (r) {
            if (!r.ok) throw new Error('HTTP ' + r.status);
            return r.status === 204 ? null : r.json().catch(function () { return null; });
        });
    }

    // ---------- IP-geo (best-effort, bounded by GEO_TIMEOUT_MS) ----------
    function fetchGeoWithTimeout() {
        var timeoutP = new Promise(function (res) { setTimeout(function () { res(null); }, GEO_TIMEOUT_MS); });

        var geoP = fetch('https://ipapi.co/json/', { credentials: 'omit' })
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (d) {
                if (!d || !d.country) return null;
                return {
                    country: (d.country || '').toUpperCase() || null,
                    region: d.region || null,
                    city: d.city || null,
                    timezone: d.timezone || null,
                };
            })
            .catch(function () { return null; });

        return Promise.race([geoP, timeoutP]);
    }

    // ---------- session + pageview ----------
    function startSession(geo) {
        var params = new URLSearchParams(location.search);
        var tz = null;
        try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (_) { }

        var body = {
            visitor_uuid: visitorUuid,
            entry_url: location.href,
            referrer: document.referrer || null,
            utm_source: params.get('utm_source'),
            utm_medium: params.get('utm_medium'),
            utm_campaign: params.get('utm_campaign'),
            utm_term: params.get('utm_term'),
            utm_content: params.get('utm_content'),
            screen_width: screen.width,
            screen_height: screen.height,
            viewport_width: window.innerWidth,
            viewport_height: window.innerHeight,
            language: navigator.language,
            timezone: (geo && geo.timezone) || tz,
        };
        if (geo) {
            body.country = geo.country;
            body.region = geo.region;
            body.city = geo.city;
        }

        return post('/session', body).then(function (r) {
            if (r && r.session_uuid) {
                sessionUuid = r.session_uuid;
                writeSessionId(sessionUuid);
                // Commit the activity timestamp immediately so a fast navigation
                // before touch() fires doesn't leave the next page load with a
                // missing timestamp (which would otherwise nuke this session).
                writeLastActivity(nowIso());
            }
        });
    }

    function openPageView() {
        pageEnteredAtMs = Date.now();
        maxScroll = 0;
        // Reset engagement state for the new pageview. If the user is here and
        // active right now (visible + recent input), start the counter now so
        // we don't lose the first few seconds waiting for the next input event.
        engagedMsAccum = 0;
        lastEngagedTickMs = null;
        if (document.visibilityState === 'visible' &&
            (perfNow() - lastInputTs) < IDLE_THRESHOLD_MS) {
            lastEngagedTickMs = perfNow();
        }
        return post('/pageview', {
            session_uuid: sessionUuid,
            url: location.href,
            url_path: location.pathname,
            url_query: location.search.replace(/^\?/, '') || null,
            referrer_url: document.referrer || null,
            page_title: document.title,
            previous_page_view_uuid: previousPageViewUuid,
            entered_at: new Date(pageEnteredAtMs).toISOString(),
        }).then(function (r) {
            if (r && r.page_view_uuid) {
                pageViewUuid = r.page_view_uuid;
                ssSet('clarity_page_view_uuid', pageViewUuid);
            }
            touch();
        });
    }

    // ---------- flush + heartbeat ----------
    function flush(useBeacon) {
        if (isBlocked || !pageViewUuid || buffer.length === 0) return Promise.resolve();
        var events = buffer.splice(0, MAX_BATCH);
        return post('/events', {
            session_uuid: sessionUuid,
            page_view_uuid: pageViewUuid,
            events: events,
        }, useBeacon).catch(function () {
            // Requeue on failure, keeping buffer bounded.
            for (var i = events.length - 1; i >= 0 && buffer.length < MAX_BATCH; i--) {
                buffer.unshift(events[i]);
            }
        });
    }

    // ---------- engaged-time tracking ----------
    // Local accumulator, in ms, of time the tab was visible AND the user was
    // active (last input within IDLE_THRESHOLD_MS). Server can't compute this
    // — only the browser knows focus + input state. Reset to 0 after each
    // successful send. Mirrors GA4's engagement definition.
    var engagedMsAccum = 0;
    // performance.now() at the moment the timer last started/ticked. `null`
    // means paused (tab hidden or user idle).
    var lastEngagedTickMs = null;
    // performance.now() of the most recent user input event. Initialised at
    // boot — page load implies the user just interacted to get here.
    var lastInputTs = (typeof performance !== 'undefined' && performance.now) ? performance.now() : 0;
    var INPUT_EVENTS = ['mousemove', 'scroll', 'keydown', 'touchstart', 'click'];

    function perfNow() {
        return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
    }

    // Advance the engaged counter up to "now", capping at the idle cutoff so a
    // stuck timer can't inflate the value if this function is called long after
    // the user went idle. Pauses the timer if we've crossed that cutoff.
    function accrueEngagedTime() {
        if (lastEngagedTickMs === null) return;
        var now = perfNow();
        var idleCutoff = lastInputTs + IDLE_THRESHOLD_MS;
        var effectiveNow = now < idleCutoff ? now : idleCutoff;
        if (effectiveNow > lastEngagedTickMs) {
            engagedMsAccum += effectiveNow - lastEngagedTickMs;
        }
        if (now >= idleCutoff) {
            lastEngagedTickMs = null; // idle — pause
        } else {
            lastEngagedTickMs = now;
        }
    }

    function pauseEngaged() {
        accrueEngagedTime();
        lastEngagedTickMs = null;
    }

    // Called from every input event (bound with { passive: true, capture: true }).
    // Refreshes the idle timestamp and, if the tab is visible and the counter
    // is paused, resumes it.
    function onInputEvent() {
        lastInputTs = perfNow();
        if (lastEngagedTickMs === null && document.visibilityState === 'visible' && !isBlocked && !pageBlocked) {
            lastEngagedTickMs = lastInputTs;
        }
    }

    // Snapshot the current delta, reset the accumulator, and return it clamped
    // to the heartbeat interval (a stuck timer must not inflate a single beat).
    function consumeEngagedDelta() {
        accrueEngagedTime();
        var delta = Math.floor(engagedMsAccum);
        engagedMsAccum = 0;
        if (delta < 0) delta = 0;
        if (delta > HEARTBEAT_INTERVAL_MS) delta = HEARTBEAT_INTERVAL_MS;
        return delta;
    }

    function heartbeat() {
        if (isBlocked || !pageViewUuid || document.hidden) return;
        var timeOnPage = Math.floor((Date.now() - pageEnteredAtMs) / 1000);
        var delta = consumeEngagedDelta();
        post('/heartbeat', {
            session_uuid: sessionUuid,
            page_view_uuid: pageViewUuid,
            time_on_page_seconds: timeOnPage,
            max_scroll_depth: maxScroll,
            engaged_ms_delta: delta,
        }).catch(function () { });
    }

    // Beacon-flavoured heartbeat — used from visibilitychange:hidden and
    // pagehide so the last accumulated delta survives page unload. Without
    // this, the tail 10–15 s of every session goes uncounted because a
    // regular fetch is cancelled during unload.
    function heartbeatBeacon() {
        if (isBlocked || !pageViewUuid) return;
        pauseEngaged();
        var delta = consumeEngagedDelta();
        if (delta <= 0) return;
        var timeOnPage = Math.floor((Date.now() - pageEnteredAtMs) / 1000);
        post('/heartbeat', {
            session_uuid: sessionUuid,
            page_view_uuid: pageViewUuid,
            time_on_page_seconds: timeOnPage,
            max_scroll_depth: maxScroll,
            engaged_ms_delta: delta,
        }, true);
    }

    // ---------- listeners ----------
    function getScrollDepth() {
        var h = document.documentElement;
        var denom = h.scrollHeight || 1;
        return Math.min(100, Math.round(((h.scrollTop + window.innerHeight) / denom) * 100));
    }

    function safeClassName(el) {
        var c = el && el.className;
        if (typeof c === 'string') return c;
        if (c && typeof c.baseVal === 'string') return c.baseVal; // SVG
        return null;
    }

    // ---------- image upload & API/form error interception ----------
    // The quote form's file input triggers uploads to Cloudinary and to the
    // in-house imagescloud service. We passively listen for their responses
    // and remember the resulting URLs so they can be attached to the form
    // submission as `uploadImagesURL` (and mirrored as a `form_input` event so
    // abandoned-form recovery has them too).
    var UPLOAD_URL_PATTERNS = [
        /api\.cloudinary\.com\/.+\/image\/upload/i,
        /imagescloud(?:v\d+)?\.codeblue\.agency\/api\/image\/upload/i,
    ];
    var uploadedImageUrls = [];

    var SUBMISSION_URL_PATTERNS = [
        /(?:submissions|forms)\.[a-z0-9.-]+/i,
        /signsincv2\.codeblue\.agency/i,
        /\/api\/leads/i,
        /\/api\/prospects/i,
        /\/api\/submit/i,
        /\/api\/no-approval/i,
        /\/api\/underfivehundred/i,
        /\/api\/dummy/i,
        /\/contact/i
    ];

    var lastFormSubmission = null; // { name, selector, timestamp }
    var recentErrorHashes = {};

    function isClarityUrl(url) {
        if (!url) return false;
        var s = String(url);
        return s.indexOf('/api/tracker') !== -1 ||
            s.indexOf('clarity.') !== -1 ||
            s.indexOf('ipapi.co') !== -1;
    }

    function isUploadEndpoint(url) {
        if (!url) return false;
        var s = String(url);
        for (var i = 0; i < UPLOAD_URL_PATTERNS.length; i++) {
            if (UPLOAD_URL_PATTERNS[i].test(s)) return true;
        }
        return false;
    }

    function isSubmissionEndpoint(url) {
        if (!url) return false;
        var s = String(url);
        for (var i = 0; i < SUBMISSION_URL_PATTERNS.length; i++) {
            if (SUBMISSION_URL_PATTERNS[i].test(s)) return true;
        }
        return false;
    }

    function isDuplicateError(key) {
        var now = Date.now();
        if (recentErrorHashes[key] && (now - recentErrorHashes[key]) < 3000) {
            return true;
        }
        recentErrorHashes[key] = now;
        var keys = Object.keys(recentErrorHashes);
        if (keys.length > 50) {
            for (var i = 0; i < keys.length; i++) {
                if (now - recentErrorHashes[keys[i]] > 15000) {
                    delete recentErrorHashes[keys[i]];
                }
            }
        }
        return false;
    }

    function recordApiError(opts) {
        if (isBlocked || pageBlocked) return;
        opts = opts || {};
        var url = opts.url ? String(opts.url) : '';
        if (isClarityUrl(url)) return; // Never record errors on Clarity's own endpoints

        var now = Date.now();
        var isRecentSubmit = lastFormSubmission && (now - lastFormSubmission.timestamp) < 30000;
        var formName = (lastFormSubmission && lastFormSubmission.name) || opts.form_name || null;
        var formSelector = (lastFormSubmission && lastFormSubmission.selector) || opts.form_selector || null;

        var isRelevant = isSubmissionEndpoint(url) || isRecentSubmit || opts.is_form_error;
        if (!isRelevant && !opts.force) return;

        var errorMsg = opts.message || opts.status_text || 'API Error';
        if (typeof errorMsg !== 'string') errorMsg = String(errorMsg);
        if (errorMsg.length > 500) errorMsg = errorMsg.slice(0, 500);

        var dedupeKey = [url ? url.split('?')[0] : '', opts.status || 0, errorMsg.slice(0, 80)].join('|');
        if (isDuplicateError(dedupeKey)) return;

        var meta = {
            endpoint: url ? url.split('?')[0] : null,
            full_url: url ? url.slice(0, 300) : null,
            http_status: opts.status || null,
            status_text: opts.status_text || null,
            error_type: opts.error_type || 'api_error',
            error_message: errorMsg,
            form_name: formName,
            form_selector: formSelector,
            time_since_submit_attempt_ms: isRecentSubmit ? (now - lastFormSubmission.timestamp) : null
        };

        if (opts.extra_meta) {
            for (var k in opts.extra_meta) {
                if (Object.prototype.hasOwnProperty.call(opts.extra_meta, k)) {
                    meta[k] = opts.extra_meta[k];
                }
            }
        }

        var eventType = isRelevant ? 'form_submit_error' : 'api_error';
        push(eventType, {
            element_id: opts.element_id || null,
            element_name: formName || null,
            meta: meta
        });

        // Flush immediately so error is reported without waiting for 5s interval
        try { flush(); } catch (_) { }
    }

    function extractUploadUrls(data) {
        var out = [];
        if (!data || typeof data !== 'object') return out;
        // Cloudinary: { secure_url, url, ... }
        if (typeof data.secure_url === 'string') out.push(data.secure_url);
        else if (typeof data.url === 'string') out.push(data.url);
        // imagescloud: { imageUrls: [...] }
        if (Array.isArray(data.imageUrls)) {
            for (var i = 0; i < data.imageUrls.length; i++) {
                if (typeof data.imageUrls[i] === 'string') out.push(data.imageUrls[i]);
            }
        }
        return out;
    }

    function recordUploadResponse(rawText) {
        if (!rawText) return;
        var data;
        try { data = JSON.parse(rawText); } catch (_) { return; }
        var urls = extractUploadUrls(data);
        if (!urls.length) return;

        var changed = false;
        for (var i = 0; i < urls.length; i++) {
            if (uploadedImageUrls.indexOf(urls[i]) === -1) {
                uploadedImageUrls.push(urls[i]);
                changed = true;
            }
        }
        if (!changed) return;

        // Mirror the running list as a form_input so abandoned-form recovery
        // shows the uploaded URLs even if the user never submits.
        var joined = uploadedImageUrls.join(',');
        push('form_input', {
            element_name: 'uploadImagesURL',
            form_field_type: 'text',
            meta: { value: joined, length: joined.length },
        });
    }

    // Patch fetch — wrap, never mutate. Observes uploads, submission APIs, and errors.
    (function patchFetch() {
        if (typeof window.fetch !== 'function') return;
        var origFetch = window.fetch;
        window.fetch = function (input, init) {
            var url = typeof input === 'string' ? input : (input && input.url);
            var watchUpload = isUploadEndpoint(url);
            var isClarity = isClarityUrl(url);

            var p;
            try { p = origFetch.apply(this, arguments); }
            catch (e) {
                if (!isClarity) {
                    recordApiError({
                        url: url,
                        error_type: 'fetch_synchronous_exception',
                        message: e && e.message ? e.message : String(e),
                        is_form_error: isSubmissionEndpoint(url)
                    });
                }
                return Promise.reject(e);
            }
            if (!p || typeof p.then !== 'function') return p;

            return p.then(function (res) {
                try {
                    if (isClarity) return res;

                    // 1. Passive upload tracking (Cloudinary / imagescloud)
                    if (watchUpload && res && typeof res.clone === 'function') {
                        res.clone().text()
                            .then(function (t) { try { recordUploadResponse(t); } catch (_) { } })
                            .catch(function () { });
                    }

                    // 2. HTTP error status (>= 400)
                    if (res && (!res.ok || res.status >= 400)) {
                        var status = res.status;
                        var statusText = res.statusText;
                        if (typeof res.clone === 'function') {
                            res.clone().text().then(function (t) {
                                var parsedMsg = null;
                                try {
                                    var j = JSON.parse(t);
                                    parsedMsg = (j && (j.message || j.error || (j.errors && JSON.stringify(j.errors)))) || null;
                                } catch (_) { }
                                recordApiError({
                                    url: url,
                                    status: status,
                                    status_text: statusText,
                                    error_type: 'http_error_response',
                                    message: parsedMsg || (t ? t.slice(0, 300) : ('HTTP ' + status)),
                                    is_form_error: isSubmissionEndpoint(url)
                                });
                            }).catch(function () {
                                recordApiError({
                                    url: url,
                                    status: status,
                                    status_text: statusText,
                                    error_type: 'http_error_response',
                                    message: 'HTTP ' + status,
                                    is_form_error: isSubmissionEndpoint(url)
                                });
                            });
                        } else {
                            recordApiError({
                                url: url,
                                status: status,
                                status_text: statusText,
                                error_type: 'http_error_response',
                                message: 'HTTP ' + status,
                                is_form_error: isSubmissionEndpoint(url)
                            });
                        }
                    } else if (res && res.ok && isSubmissionEndpoint(url) && typeof res.clone === 'function') {
                        // 3. Payload-level error inside an HTTP 200 response (e.g. status: 422 or success: false)
                        res.clone().text().then(function (t) {
                            try {
                                var j = JSON.parse(t);
                                if (j && (j.status === 422 || j.status === 'error' || j.success === false)) {
                                    var errMsg = j.message || (j.errors && JSON.stringify(j.errors)) || j.error || 'Submission rejected by server';
                                    recordApiError({
                                        url: url,
                                        status: j.status || 422,
                                        status_text: 'Unprocessable Entity',
                                        error_type: 'api_payload_error',
                                        message: typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg),
                                        is_form_error: true
                                    });
                                }
                            } catch (_) { }
                        }).catch(function () { });
                    }
                } catch (_) { }
                return res;
            }, function (err) {
                // Network error / offline / CORS / DNS failure
                try {
                    if (!isClarity) {
                        recordApiError({
                            url: url,
                            error_type: 'network_failure',
                            message: (err && err.message) ? err.message : 'Network request failed',
                            is_form_error: isSubmissionEndpoint(url)
                        });
                    }
                } catch (_) { }
                return Promise.reject(err);
            });
        };
    })();

    // Patch XMLHttpRequest — observes upload responses, status >= 400, and network errors.
    (function patchXHR() {
        if (typeof XMLHttpRequest === 'undefined') return;
        var origOpen = XMLHttpRequest.prototype.open;
        var origSend = XMLHttpRequest.prototype.send;
        XMLHttpRequest.prototype.open = function (method, url) {
            try {
                this.__clarityUrl = url;
                this.__clarityUpload = isUploadEndpoint(url);
                this.__clarityIsClarity = isClarityUrl(url);
            } catch (_) { }
            return origOpen.apply(this, arguments);
        };
        XMLHttpRequest.prototype.send = function () {
            var xhr = this;
            var url = xhr.__clarityUrl;
            var isClarity = xhr.__clarityIsClarity;

            if (!isClarity) {
                try {
                    xhr.addEventListener('load', function () {
                        try {
                            if (xhr.__clarityUpload && xhr.status >= 200 && xhr.status < 300) {
                                recordUploadResponse(xhr.responseText);
                            }

                            // HTTP error status
                            if (xhr.status >= 400) {
                                var msg = null;
                                try {
                                    var j = JSON.parse(xhr.responseText);
                                    msg = (j && (j.message || j.error || (j.errors && JSON.stringify(j.errors)))) || null;
                                } catch (_) { }
                                recordApiError({
                                    url: url,
                                    status: xhr.status,
                                    status_text: xhr.statusText,
                                    error_type: 'xhr_http_error',
                                    message: msg || (xhr.responseText ? xhr.responseText.slice(0, 300) : ('HTTP ' + xhr.status)),
                                    is_form_error: isSubmissionEndpoint(url)
                                });
                            } else if (xhr.status >= 200 && xhr.status < 300 && isSubmissionEndpoint(url)) {
                                // Payload-level error inside HTTP 200
                                try {
                                    var j2 = JSON.parse(xhr.responseText);
                                    if (j2 && (j2.status === 422 || j2.status === 'error' || j2.success === false)) {
                                        var errMsg2 = j2.message || (j2.errors && JSON.stringify(j2.errors)) || j2.error || 'Submission rejected by server';
                                        recordApiError({
                                            url: url,
                                            status: j2.status || 422,
                                            status_text: 'Unprocessable Entity',
                                            error_type: 'xhr_payload_error',
                                            message: typeof errMsg2 === 'string' ? errMsg2 : JSON.stringify(errMsg2),
                                            is_form_error: true
                                        });
                                    }
                                } catch (_) { }
                            }
                        } catch (_) { }
                    });

                    xhr.addEventListener('error', function () {
                        try {
                            recordApiError({
                                url: url,
                                error_type: 'xhr_network_error',
                                message: 'XHR network error',
                                is_form_error: isSubmissionEndpoint(url)
                            });
                        } catch (_) { }
                    });

                    xhr.addEventListener('timeout', function () {
                        try {
                            recordApiError({
                                url: url,
                                error_type: 'xhr_timeout',
                                message: 'XHR request timed out',
                                is_form_error: isSubmissionEndpoint(url)
                            });
                        } catch (_) { }
                    });
                } catch (_) { }
            }
            return origSend.apply(this, arguments);
        };
    })();

    // Hook into existing theme error logger (defined in layout/theme.liquid)
    (function hookLogClientError() {
        function attachHook() {
            if (typeof window === 'undefined') return;
            var origLog = window.logClientError;
            if (origLog && origLog.__clarityHooked) return;

            window.logClientError = function (context, err, uuid) {
                try {
                    recordApiError({
                        error_type: 'client_error_logger',
                        status_text: context || null,
                        message: (err && err.message) ? err.message : String(err || 'Client error logged'),
                        is_form_error: true,
                        extra_meta: {
                            error_context: context || null,
                            lead_uuid: uuid || null
                        }
                    });
                } catch (_) { }
                if (typeof origLog === 'function') {
                    return origLog.apply(this, arguments);
                }
            };
            window.logClientError.__clarityHooked = true;
        }

        attachHook();
        if (typeof document !== 'undefined' && document.addEventListener) {
            document.addEventListener('DOMContentLoaded', attachHook, { once: true });
        }
    })();

    // Safe console.error monitor — captures form/submission errors logged to the console
    (function hookConsoleError() {
        if (typeof console === 'undefined' || typeof console.error !== 'function') return;
        var origConsoleError = console.error;
        console.error = function () {
            try {
                var msg = '';
                for (var i = 0; i < arguments.length; i++) {
                    var a = arguments[i];
                    if (typeof a === 'string') {
                        msg += a + ' ';
                    } else if (a && a.message) {
                        msg += a.message + ' ';
                    } else {
                        try { msg += JSON.stringify(a) + ' '; } catch (_) { msg += String(a) + ' '; }
                    }
                }
                var isSubmissionLog = /submission|submit|lead_form|quoteForm|contactForm|422|Failed to fetch/i.test(msg);
                var isRecentSubmit = lastFormSubmission && (Date.now() - lastFormSubmission.timestamp) < 30000;

                if ((isSubmissionLog && isRecentSubmit) || (isSubmissionLog && /Error in submission|submit.*fail/i.test(msg))) {
                    recordApiError({
                        error_type: 'console_error',
                        message: msg.slice(0, 400),
                        is_form_error: true
                    });
                }
            } catch (_) { }
            return origConsoleError.apply(console, arguments);
        };
    })();

    // ---------- form auto-capture ----------
    // Matches native submit buttons AND common "fake submit" buttons that use
    // type="button" + a custom click handler (e.g. Signize's .submitButton,
    // signs.inc's .qf-submit / .cbf-qf-submit which POST via fetch instead of
    // firing a native submit event).
    var SUBMIT_BTN_SELECTOR =
        'button[type="submit"], input[type="submit"], input[type="image"], ' +
        '.submitButton, .qf-submit, .cbf-qf-submit, [data-clarity-submit]';

    // Site pattern: buttons/cards that toggle a `.sel` (or `.cbf-sel`) class
    // inside a container tagged with `data-name`. Used by the quote form's
    // Indoor/Outdoor toggles and mockup-type picker.
    var SITE_TOGGLE_SELECTOR = '.qf-toggle-btn, .cbf-qf-toggle-btn';
    var SITE_CARD_SELECTOR = '.qf-mockup-card, .cbf-qf-mockup-card';
    function isSelectedNode(el) {
        return !!(el && el.classList && (el.classList.contains('sel') || el.classList.contains('cbf-sel')));
    }
    // Per-form dedupe window: 2 s covers "click fires + custom JS calls form.submit()".
    var recentlySubmitted = new WeakMap();

    function serializeForm(form) {
        var fields = {};
        try {
            var els = form.elements || [];
            for (var i = 0; i < els.length; i++) {
                var el = els[i];
                if (!el || !el.name) continue;
                if (el.disabled) continue;
                if (el.type === 'password') continue;
                if (el.getAttribute && el.getAttribute('data-clarity-ignore') !== null) continue;

                var name = el.name;
                var type = el.type;
                var tag = el.tagName;

                if (type === 'checkbox' || type === 'radio') {
                    if (!el.checked) continue;
                    var val = (el.value != null && el.value !== '') ? el.value : true;
                    if (fields[name] === undefined) {
                        fields[name] = val;
                    } else {
                        if (!Array.isArray(fields[name])) fields[name] = [fields[name]];
                        fields[name].push(val);
                    }
                } else if (type === 'file') {
                    var files = [];
                    var fl = el.files || [];
                    for (var j = 0; j < fl.length; j++) {
                        files.push({ filename: fl[j].name, mime_type: fl[j].type, size_bytes: fl[j].size });
                    }
                    if (files.length === 1) fields[name] = files[0];
                    else if (files.length > 1) fields[name] = files;
                } else if (tag === 'SELECT' && el.multiple) {
                    var opts = [];
                    var so = el.selectedOptions || [];
                    for (var k = 0; k < so.length; k++) opts.push(so[k].value);
                    fields[name] = opts;
                } else {
                    fields[name] = el.value;
                }
            }
        } catch (_) { }

        // Site pattern: containers marked with `data-name` hold buttons/cards
        // that mark selection via `.sel` / `.cbf-sel` (Indoor/Outdoor toggle,
        // mockup-type picker). There's no matching <input>, so serializeForm
        // would otherwise miss these values entirely.
        try {
            var selectionGroups = form.querySelectorAll('[data-name]');
            for (var g = 0; g < selectionGroups.length; g++) {
                var groupEl = selectionGroups[g];
                var groupName = groupEl.getAttribute('data-name');
                if (!groupName || fields[groupName] !== undefined) continue;
                var selectedNodes = groupEl.querySelectorAll('.sel, .cbf-sel');
                if (!selectedNodes.length) continue;
                var vals = [];
                for (var s = 0; s < selectedNodes.length; s++) {
                    var v = selectedNodes[s].dataset && selectedNodes[s].dataset.val;
                    if (v) vals.push(v);
                }
                if (vals.length === 1) fields[groupName] = vals[0];
                else if (vals.length > 1) fields[groupName] = vals;
            }
        } catch (_) { }

        // Attach any image URLs captured from Cloudinary / imagescloud uploads
        // as a comma-separated string.
        if (uploadedImageUrls.length && fields.uploadImagesURL === undefined) {
            fields.uploadImagesURL = uploadedImageUrls.join(',');
        }

        return fields;
    }

    function formNameFor(form) {
        return (form.dataset && form.dataset.trackName) ||
            form.getAttribute('name') ||
            form.id ||
            'form';
    }

    function formSelectorFor(form) {
        if (form.id) return '#' + form.id;
        var n = form.getAttribute('name');
        return n ? 'form[name="' + n + '"]' : null;
    }

    function pickField(fields, keys, fuzzyRegex) {
        for (var i = 0; i < keys.length; i++) {
            var v = fields[keys[i]];
            if (typeof v === 'string' && v.length) return v;
        }
        if (fuzzyRegex) {
            for (var k in fields) {
                if (!Object.prototype.hasOwnProperty.call(fields, k)) continue;
                if (fuzzyRegex.test(k) && typeof fields[k] === 'string' && fields[k].length) return fields[k];
            }
        }
        return null;
    }

    function doFormSubmit(form, wasComplete) {
        if (!form || isBlocked || pageBlocked) return;
        if (!sessionUuid || !pageViewUuid) return;

        var now = Date.now();
        var last = recentlySubmitted.get(form);
        if (last && (now - last) < 2000) return; // dedupe submit-vs-click double fire
        recentlySubmitted.set(form, now);

        var fields = serializeForm(form);
        var name = formNameFor(form);
        var selector = formSelectorFor(form);

        lastFormSubmission = {
            name: name,
            selector: selector,
            timestamp: Date.now()
        };

        push('form_submit_success', {
            meta: { form_name: name, form_selector: selector },
        });

        post('/form-submit', {
            session_uuid: sessionUuid,
            page_view_uuid: pageViewUuid,
            form_name: name,
            form_selector: selector,
            fields: fields,
            was_complete: wasComplete !== false,
            submitted_at: nowIso(),
        }, true);

        var email = pickField(fields, ['email', 'Email', 'EMAIL', 'user_email', 'contact_email', 'email_address'], /email/i);
        if (email) {
            post('/identify', {
                session_uuid: sessionUuid,
                email: email,
                name: pickField(fields, ['name', 'your_name', 'full_name', 'fullName', 'first_name', 'firstName']),
                phone: pickField(fields, ['phone', 'phone_number', 'phoneNumber', 'tel', 'mobile', 'contact_number']),
                meta: null,
            }, true);
        }

        // Flush any pending events with beacon so they survive a full-page nav after submit.
        flush(true);

        // Reset upload accumulator so a subsequent submission on the same page
        // doesn't carry stale URLs.
        uploadedImageUrls = [];
    }

    function trackClick(e) {
        var raw = e.target;
        if (!raw || raw.nodeType !== 1) return;
        // Toggles/selects have their own handler.
        if (raw.closest && raw.closest('[data-toggle],[data-select],' + SITE_TOGGLE_SELECTOR + ',' + SITE_CARD_SELECTOR)) return;

        var el = (raw.closest && raw.closest('a, button, [data-track], [role="button"], [role="link"], input[type="submit"], input[type="button"]')) || raw;
        var text = (el.innerText || el.textContent || '').trim().slice(0, 512) || null;
        var href = el.href || null;

        push('click', {
            element_tag: (el.tagName || '').toLowerCase() || null,
            element_id: el.id || null,
            element_classes: safeClassName(el),
            element_text: text,
            element_href: href,
            viewport_x: e.clientX,
            viewport_y: e.clientY,
            meta: { x: e.clientX, y: e.clientY, href: href },
        });
    }

    function trackToggleOrSelect(e) {
        var t = e.target;
        if (!t || !t.closest) return;

        // Site pattern: .qf-toggle-btn / .cbf-qf-toggle-btn inside a container
        // with `data-name`. Class flips to `.sel` / `.cbf-sel` on click.
        var siteToggle = t.closest(SITE_TOGGLE_SELECTOR);
        if (siteToggle) {
            var siteToggleGroup = siteToggle.closest('[data-name]');
            setTimeout(function () {
                var on = isSelectedNode(siteToggle);
                push('toggle', {
                    element_id: siteToggle.id || null,
                    element_tag: (siteToggle.tagName || '').toLowerCase(),
                    meta: {
                        group: siteToggleGroup ? siteToggleGroup.getAttribute('data-name') : null,
                        value: (siteToggle.dataset && siteToggle.dataset.val) || null,
                        state: on ? 'on' : 'off',
                    },
                });
            }, 0);
            return;
        }

        // Site pattern: mockup cards — multi-select via `.sel` / `.cbf-sel`.
        var siteCard = t.closest(SITE_CARD_SELECTOR);
        if (siteCard) {
            var siteCardGroup = siteCard.closest('[data-name]');
            setTimeout(function () {
                var selected = isSelectedNode(siteCard);
                push(selected ? 'select' : 'deselect', {
                    element_id: siteCard.id || null,
                    element_tag: (siteCard.tagName || '').toLowerCase(),
                    meta: {
                        group: siteCardGroup ? siteCardGroup.getAttribute('data-name') : null,
                        value: (siteCard.dataset && siteCard.dataset.val) || null,
                        selected: selected,
                    },
                });
            }, 0);
            return;
        }

        var toggle = t.closest('[data-toggle]');
        if (toggle) {
            // Read state on next tick so consumers can flip classes/aria first.
            setTimeout(function () {
                var on = toggle.classList.contains('is-active') ||
                    toggle.getAttribute('aria-pressed') === 'true';
                push('toggle', {
                    element_id: toggle.id || null,
                    element_tag: toggle.tagName.toLowerCase(),
                    meta: {
                        group: toggle.dataset.toggle,
                        value: toggle.dataset.value || null,
                        state: on ? 'on' : 'off',
                    },
                });
            }, 0);
            return;
        }

        var sel = t.closest('[data-select]');
        if (sel) {
            setTimeout(function () {
                var selected = sel.classList.contains('is-selected') ||
                    sel.getAttribute('aria-selected') === 'true';
                push(selected ? 'select' : 'deselect', {
                    element_id: sel.id || null,
                    element_tag: sel.tagName.toLowerCase(),
                    meta: {
                        group: sel.dataset.select,
                        value: sel.dataset.value || null,
                        selected: selected,
                    },
                });
            }, 0);
        }
    }

    function isTrackableField(el) {
        if (!el || !el.matches) return false;
        if (!el.matches('input, textarea, select')) return false;
        if (el.type === 'password') return false;
        if (el.getAttribute && el.getAttribute('data-clarity-ignore') !== null) return false;
        return true;
    }

    function attachListeners() {
        if (listenersAttached) return;
        listenersAttached = true;

        // Scroll milestones (25/50/75/100 fired once per page view).
        var milestones = [25, 50, 75, 100];
        var fired = new Set();
        window.addEventListener('scroll', function () {
            var depth = getScrollDepth();
            if (depth > maxScroll) maxScroll = depth;
            for (var i = 0; i < milestones.length; i++) {
                var m = milestones[i];
                if (depth >= m && !fired.has(m)) {
                    fired.add(m);
                    push('scroll', { scroll_depth: m, meta: { depth: m } });
                }
            }
        }, { passive: true });

        // Reset scroll milestones on SPA route change.
        window.addEventListener('clarity:pageview-open', function () { fired.clear(); });

        // Clicks (capture so we still see cancelled clicks).
        document.addEventListener('click', trackClick, true);

        // Toggles / multi-selects.
        document.addEventListener('click', trackToggleOrSelect);

        // Real form submits — fires for <button type="submit">, <input type="submit">,
        // and Enter-key submits. Passive: we never preventDefault, so form behaviour
        // is untouched.
        document.addEventListener('submit', function (e) {
            if (isBlocked || pageBlocked) return;
            var form = e.target;
            if (!form || form.tagName !== 'FORM') return;
            try { doFormSubmit(form, true); } catch (_) { }
        }, true);

        // Submit-button clicks — covers forms whose "submit" button is
        // type="button" with a custom JS handler (e.g. Signize's .submitButton).
        // Emits form_submit_attempt for every submit-looking click. If it's a
        // real submit button, the browser's submit event will handle serialization
        // (deduped by recentlySubmitted). Otherwise we snapshot fields on click.
        document.addEventListener('click', function (e) {
            if (isBlocked || pageBlocked) return;
            var raw = e.target;
            if (!raw || !raw.closest) return;
            var btn = raw.closest(SUBMIT_BTN_SELECTOR);
            if (!btn) return;
            var form = btn.closest('form');
            if (!form) return;

            try {
                var submitFormName = formNameFor(form);
                var submitFormSelector = formSelectorFor(form);
                lastFormSubmission = {
                    name: submitFormName,
                    selector: submitFormSelector,
                    timestamp: Date.now()
                };

                push('form_submit_attempt', {
                    element_id: btn.id || null,
                    element_tag: (btn.tagName || '').toLowerCase(),
                    element_classes: safeClassName(btn),
                    element_text: ((btn.innerText || btn.textContent || '').trim().slice(0, 512)) || null,
                    meta: {
                        form_name: submitFormName,
                        form_selector: submitFormSelector,
                    },
                });

                // <button> with no type attr defaults to submit; skip serialization here —
                // the native submit event will do it.
                var btnType = (btn.getAttribute && btn.getAttribute('type')) || '';
                var isRealSubmit =
                    (btn.tagName === 'BUTTON' && btnType !== 'button') ||
                    btnType === 'submit' ||
                    btnType === 'image';
                if (isRealSubmit) return;

                // Fake-submit case: let any inline click handler mutate the form
                // first (e.g. append fields), then snapshot.
                setTimeout(function () {
                    try { doFormSubmit(form, true); } catch (_) { }
                }, 50);
            } catch (_) { }
        }, true);

        // Form fields.
        var debounceMap = new WeakMap();

        document.addEventListener('focusin', function (e) {
            var el = e.target;
            if (!isTrackableField(el)) return;
            push('form_focus', {
                element_id: el.id || null,
                element_name: el.name || null,
                form_field_type: el.type || 'text',
            });
        });

        document.addEventListener('focusout', function (e) {
            var el = e.target;
            if (!isTrackableField(el)) return;
            var filled = !!(el.value && String(el.value).length);
            push('form_blur', {
                element_id: el.id || null,
                element_name: el.name || null,
                form_field_type: el.type || 'text',
                meta: { filled: filled },
            });
        });

        document.addEventListener('input', function (e) {
            var el = e.target;
            if (!isTrackableField(el)) return;
            var t = debounceMap.get(el);
            if (t) clearTimeout(t);
            debounceMap.set(el, setTimeout(function () {
                var rawVal = (el.type === 'checkbox' || el.type === 'radio') ? el.checked : el.value;
                var strVal = rawVal === null || rawVal === undefined ? '' : String(rawVal);
                push('form_input', {
                    element_id: el.id || null,
                    element_name: el.name || null,
                    form_field_type: el.type || 'text',
                    meta: { value: strVal, length: strVal.length },
                });
            }, 500));
        });

        // File uploads.
        document.addEventListener('change', function (e) {
            var el = e.target;
            if (!el || el.tagName !== 'INPUT' || el.type !== 'file') return;
            var f = el.files && el.files[0];
            if (!f) return;

            if (f.size > FILE_MAX_BYTES) {
                push('file_error', {
                    element_id: el.id || null,
                    element_name: el.name || null,
                    meta: { filename: f.name, reason: 'size_over_limit', size_bytes: f.size },
                });
                return;
            }
            var extMatch = /\.([a-z0-9]+)$/i.exec(f.name || '');
            var ext = extMatch ? extMatch[1].toLowerCase() : '';
            var mimeOk = !f.type || OK_FILE_MIMES.indexOf(f.type) !== -1;
            var extOk = ext && OK_FILE_EXTS.indexOf(ext) !== -1;
            if (!mimeOk && !extOk) {
                push('file_error', {
                    element_id: el.id || null,
                    element_name: el.name || null,
                    meta: { filename: f.name, reason: 'invalid_type', size_bytes: f.size },
                });
                return;
            }
            push('file_upload', {
                element_id: el.id || null,
                element_name: el.name || null,
                meta: { filename: f.name, mime_type: f.type, size_bytes: f.size },
            });
        });

        // Clipboard.
        document.addEventListener('copy', function () {
            var length = 0;
            try {
                var s = document.getSelection && document.getSelection().toString();
                length = s ? s.length : 0;
            } catch (_) { }
            push('copy', { meta: { length: length } });
        });

        // Resize (debounced).
        var resizeT;
        window.addEventListener('resize', function () {
            clearTimeout(resizeT);
            resizeT = setTimeout(function () {
                push('resize', { meta: { width: window.innerWidth, height: window.innerHeight } });
            }, 300);
        });

        // Visibility. Engaged timer must pause the instant the tab hides — the
        // idle threshold alone would otherwise credit up to 30 s of phantom
        // engagement to a backgrounded tab.
        document.addEventListener('visibilitychange', function () {
            push('visibility_change', { meta: { state: document.hidden ? 'hidden' : 'visible' } });
            if (document.hidden) {
                heartbeatBeacon(); // pauses + flushes pending delta via sendBeacon
                flush();
            }
            // On return-to-visible we deliberately don't auto-resume — a fresh
            // input event will (see onInputEvent above).
        });

        // Page exit → flush engagement first, then close the pageview. Both use
        // sendBeacon so they survive unload.
        window.addEventListener('pagehide', function () {
            heartbeatBeacon();
            closePageView('close', true);
        });

        // Engaged-time input tracking. capture:true so we still see events that
        // a downstream handler stops propagation on; passive:true so we never
        // interfere with the page's own scroll/touch behaviour.
        for (var iIdx = 0; iIdx < INPUT_EVENTS.length; iIdx++) {
            document.addEventListener(INPUT_EVENTS[iIdx], onInputEvent, { passive: true, capture: true });
        }
        // Treat page load itself as an implicit input — if the tab is visible
        // right now, start accruing immediately rather than waiting for the
        // first mouse twitch.
        if (document.visibilityState === 'visible') {
            lastInputTs = perfNow();
            lastEngagedTickMs = lastInputTs;
        }

        // Idle-timeout recycle. Recycle *before* pushing the activity so the
        // event lands on the fresh session.
        var recycleIfStale = function () {
            var last = readLastActivity();
            var lastMs = last ? Date.parse(last) : 0;
            if (lastMs && (Date.now() - lastMs) > SESSION_TIMEOUT_MS) {
                clearSessionId();
                sessionUuid = null;
                boot();
            } else {
                touch();
            }
        };
        ['mousedown', 'keydown', 'touchstart'].forEach(function (t) {
            document.addEventListener(t, recycleIfStale, { passive: true });
        });

        // SPA route change: patch history + popstate.
        patchHistory();
    }

    function closePageView(exitType, useBeacon) {
        if (isBlocked || !pageViewUuid) return Promise.resolve();
        var timeOnPage = Math.floor((Date.now() - pageEnteredAtMs) / 1000);
        // Flush events first so they land on this page view.
        var flushP = flush(useBeacon);
        var closeP = post('/pageview/close', {
            session_uuid: sessionUuid,
            page_view_uuid: pageViewUuid,
            left_at: nowIso(),
            time_on_page_seconds: timeOnPage,
            max_scroll_depth: maxScroll,
            exit_type: exitType,
        }, useBeacon).catch(function () { });
        return Promise.all([flushP, closeP]);
    }

    // ---------- SPA ----------
    var spaLock = false;
    function spaRouteChange() {
        if (isBlocked || spaLock) return Promise.resolve();
        spaLock = true;
        var prev = pageViewUuid;
        var timeOnPage = Math.floor((Date.now() - pageEnteredAtMs) / 1000);

        // Flush pending engagement so it lands on the closing pageview rather
        // than leaking into whichever pageview opens next.
        if (prev) heartbeatBeacon();

        var closeP = prev
            ? flush().then(function () {
                return post('/pageview/close', {
                    session_uuid: sessionUuid,
                    page_view_uuid: prev,
                    left_at: nowIso(),
                    time_on_page_seconds: timeOnPage,
                    max_scroll_depth: maxScroll,
                    exit_type: 'navigation',
                }).catch(function () { });
            })
            : Promise.resolve();

        return closeP
            .then(function () {
                if (prev) previousPageViewUuid = prev;
                pageViewUuid = null;

                // Recompute soft block for the new URL. Skip opening a pageview
                // when navigating into a blocked path; resume on the next nav out.
                pageBlocked = isPathBlocked(location.pathname);
                if (pageBlocked) return null;

                // If we booted on a blocked page, the session was never started.
                if (!sessionUuid) {
                    return startSession(null).then(function () {
                        if (!sessionUuid) return null;
                        return openPageView();
                    });
                }
                return openPageView();
            })
            .then(function () {
                if (!pageBlocked && pageViewUuid) {
                    try { window.dispatchEvent(new Event('clarity:pageview-open')); } catch (_) { }
                }
            })
            .catch(function () { })
            .then(function () { spaLock = false; });
    }

    function patchHistory() {
        var origPush = history.pushState;
        var origReplace = history.replaceState;
        history.pushState = function () {
            var r = origPush.apply(this, arguments);
            Promise.resolve().then(spaRouteChange);
            return r;
        };
        history.replaceState = function () {
            var r = origReplace.apply(this, arguments);
            Promise.resolve().then(spaRouteChange);
            return r;
        };
        window.addEventListener('popstate', function () { spaRouteChange(); });
    }

    // ---------- boot ----------
    function boot() {
        if (isBlocked) return Promise.resolve();

        pageBlocked = isPathBlocked(location.pathname);

        // Attach listeners + intervals unconditionally so that if the visitor
        // SPA-navigates from a blocked page to a tracked page, we can resume.
        attachListeners();
        if (!intervalsStarted) {
            intervalsStarted = true;
            setInterval(function () { flush(); }, FLUSH_INTERVAL_MS);
            setInterval(heartbeat, HEARTBEAT_INTERVAL_MS);
        }

        if (pageBlocked) return Promise.resolve();

        return fetchGeoWithTimeout()
            .catch(function () { return null; })
            .then(function (geo) {
                if (geo && geo.country && BLOCKED_COUNTRIES.indexOf(geo.country) !== -1) {
                    isBlocked = true;
                    return null;
                }
                if (sessionUuid) return null;
                return startSession(geo).catch(function () { });
            })
            .then(function () {
                if (isBlocked || pageBlocked || !sessionUuid) return null;
                return openPageView().catch(function () { });
            })
            .then(function () {
                if (isBlocked || pageBlocked || !pageViewUuid) return;
                try { window.dispatchEvent(new Event('clarity:pageview-open')); } catch (_) { }
            });
    }

    // ---------- public API ----------
    window.Clarity = {
        track: function (type, data) { push(type, data); },
        identify: function (email, name, phone, meta) {
            if (isBlocked || !sessionUuid) return Promise.resolve(null);
            return post('/identify', {
                session_uuid: sessionUuid,
                email: email,
                name: name || null,
                phone: phone || null,
                meta: meta || null,
            });
        },
        formSubmit: function (opts) {
            opts = opts || {};
            if (isBlocked || !sessionUuid || !pageViewUuid) return Promise.resolve(null);
            return post('/form-submit', {
                session_uuid: sessionUuid,
                page_view_uuid: pageViewUuid,
                form_name: opts.name,
                form_selector: opts.selector || null,
                fields: opts.fields || {},
                was_complete: opts.was_complete !== false,
                submitted_at: nowIso(),
            });
        },
        flush: function () { return flush(); },
        spaRouteChange: spaRouteChange,
        recordError: function (opts) { recordApiError(opts); },
        getSessionUuid: function () { return sessionUuid; },
        getPageViewUuid: function () { return pageViewUuid; },
        getVisitorUuid: function () { return visitorUuid; },
    };

    boot();
})();
